import React from "react";
import { Link } from "react-router-dom";
import {
  Layers,
  Building2,
  FactoryIcon,
  FileStack,
  Wallet,
  MapPinned,
  Network,
  ArrowUpRight,
  ArrowRight,
  ChevronRight,
} from "lucide-react";
import { PageHero, SectionWrapper, SectionHeader } from "../components/Section";

// Order intentionally aligns with Product Portfolio architecture (A → G).
// Slugs preserved from the previous routing for backward link compatibility.
// `anchor` points to the matching Product Portfolio family section.
const PATHWAYS = [
  {
    n: "01",
    slug: "structured-institutions",
    anchor: "institutional-management-platforms",
    icon: Layers,
    title: "For Universities, Hospitals, Chambers, Unions & Multi-Department Institutions",
    short:
      "For organizations with complex institutional structures, committees, internal services, documents, assets, and reporting needs that go beyond a single sector.",
    cta: "Explore Institutional Pathway",
  },
  {
    n: "02",
    slug: "municipality",
    anchor: "municipality-public-service-systems",
    icon: Building2,
    title: "For Municipalities & Local Public Administrations",
    short:
      "For institutions that deliver public-facing services while managing internal administrative, financial, operational, and document-based responsibilities.",
    cta: "Explore Municipal Pathway",
  },
  {
    n: "03",
    slug: "industrial-zone",
    anchor: "industrial-zone-utility-management",
    icon: FactoryIcon,
    title: "For Industrial Zones, Utilities & Service Operators",
    short:
      "For organized industrial zones, business parks, utility-based institutions, service operators, and estate-style operating structures with subscriber, infrastructure, and field-service needs.",
    cta: "Explore Industrial & Utility Pathway",
  },
  {
    n: "04",
    slug: "electronic-document",
    anchor: "document-records-digital-transformation",
    icon: FileStack,
    title: "For Document-Intensive & Regulated Service Environments",
    short:
      "For environments where documents, approvals, archives, correspondence, records, verification, access rights, and traceability are central to daily operations.",
    cta: "Explore Document Pathway",
  },
  {
    n: "05",
    slug: "finance-revenue",
    anchor: "finance-revenue-procurement",
    icon: Wallet,
    title: "For Finance, Revenue & Administrative Operations",
    short:
      "For internal departments and administrative teams that need stronger structure across revenue, accounting, procurement, inventory, assets, and institutional reporting.",
    cta: "Explore Administrative Operations Pathway",
  },
  {
    n: "06",
    slug: "field-infrastructure",
    anchor: "field-infrastructure-spatial-operations",
    icon: MapPinned,
    title: "For Field, Facility, Laboratory & Technical Operations",
    short:
      "For institutions that manage field teams, facilities, infrastructure, technical tasks, laboratories, sample processes, maintenance, inspections, and mobile work execution.",
    cta: "Explore Field & Laboratory Pathway",
  },
  {
    n: "07",
    slug: "portals-integrations",
    anchor: "portals-integrations-external-systems",
    icon: Network,
    title: "For Portals, Integrations & Executive Visibility",
    short:
      "For institutions that need clearer digital access channels, internal and user-facing portals, external integrations, mobile access, dashboards, and executive visibility.",
    cta: "Explore Portals & Visibility Pathway",
  },
];

const Solutions = () => (
  <main data-testid="page-solutions">
    <PageHero
      overline="Solutions"
      title="Solution pathways shaped around institutional realities."
      lead="Allinstra solutions are organized around the institutions, teams, and operational contexts that need stronger structure."
    >
      <p className="max-w-3xl text-[15px] text-ink-600 leading-relaxed">
        Instead of starting with software modules, this section starts with the type of
        organization, department, or operational challenge you represent — and shows how
        Allinstra’s product portfolio can be shaped around that need. Each solution pathway may
        combine modules from several product families. For the complete module architecture,
        visit the Product Portfolio page.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/product-portfolio" data-testid="solutions-hero-portfolio" className="btn-primary">
          View Product Portfolio <ArrowUpRight size={16} />
        </Link>
        <Link to="/contact" data-testid="solutions-hero-contact" className="btn-secondary">
          Discuss Your Institution <ArrowRight size={14} />
        </Link>
      </div>
    </PageHero>

    <SectionWrapper id="solution-pathways">
      <SectionHeader
        overline="Solution Pathways"
        title="Seven pathways. One coordinated institutional structure."
        lead="The order intentionally aligns with the Product Portfolio architecture, but each pathway is written from the perspective of institution types, operational teams, and practical use cases."
      />

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PATHWAYS.map((p) => (
          <article
            key={p.slug}
            data-testid={`pathway-card-${p.slug}`}
            className="bg-white border border-ink-200 p-7 sm:p-8 card-hover flex flex-col"
          >
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 border border-ink-200 inline-flex items-center justify-center text-cyan-600">
                <p.icon size={18} strokeWidth={1.5} />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-700">
                {p.n} / 07
              </div>
            </div>
            <h3 className="font-display text-[17px] sm:text-lg font-semibold text-ink-900 tracking-tight mt-5 leading-snug">
              {p.title}
            </h3>
            <p className="mt-3 text-[14px] leading-relaxed text-ink-600 flex-1">{p.short}</p>
            <Link
              to={`/solutions/${p.slug}`}
              data-testid={`pathway-cta-${p.slug}`}
              className="mt-6 inline-flex items-center gap-1 text-[12px] font-medium text-cyan-700 hover:text-cyan-800 uppercase tracking-widest self-start group"
            >
              {p.cta} <ChevronRight size={12} />
            </Link>
          </article>
        ))}
      </div>
    </SectionWrapper>

    {/* Cross-Portfolio Note */}
    <SectionWrapper id="solutions-cross-portfolio" alt>
      <div className="border-l-2 border-cyan-500 pl-6 max-w-4xl">
        <div className="tag-overline">Cross-Portfolio Note</div>
        <div
          data-testid="solutions-cross-portfolio-note"
          className="mt-3 space-y-4 text-[15.5px] text-ink-700 leading-relaxed"
        >
          <p>
            Each solution pathway may combine modules from multiple Allinstra product families.
            Product Portfolio presents the complete product and module architecture; Solutions
            presents how those capabilities can be shaped around real institutional scenarios.
          </p>
          <p>
            The Solutions order is intentionally aligned with the Product Portfolio
            architecture, but written from the perspective of institution types, operational
            teams, and practical use cases.
          </p>
        </div>
      </div>
    </SectionWrapper>

    {/* Implementation-Aware Note */}
    <SectionWrapper id="solutions-implementation-aware">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeader
            overline="Implementation-Aware"
            title="Built around real conditions."
          />
        </div>
        <div className="lg:col-span-7 space-y-5 text-[16px] leading-relaxed text-ink-700">
          <p>
            Allinstra solution pathways are implementation-aware. Module scope, integration
            availability, public-system connections, electronic signature use, payment
            infrastructure, document formats, language, legal framework, and technical
            feasibility may vary by country, institution, and operating environment.
          </p>
          <p className="text-ink-900">
            Before implementation, Allinstra evaluates the institution’s priorities, local
            infrastructure, available integrations, data quality, regulatory context, and
            phased rollout needs.
          </p>
        </div>
      </div>
    </SectionWrapper>

    {/* Final CTA */}
    <SectionWrapper id="solutions-final-cta" alt>
      <div className="border border-ink-200 p-8 sm:p-12 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="tag-overline">Final step</div>
            <h2 className="heading-display mt-3 text-2xl sm:text-3xl font-semibold">
              Find the pathway that fits your institution.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
            <Link to="/contact" data-testid="solutions-final-contact" className="btn-primary">
              Contact Allinstra <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/product-portfolio"
              data-testid="solutions-final-portfolio"
              className="btn-secondary"
            >
              View Product Portfolio
            </Link>
          </div>
        </div>
      </div>
    </SectionWrapper>
  </main>
);

export default Solutions;
